"use client"

import { AUTH_TOKEN } from "constants/AuthConstant"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { setCategoriesState } from "store/slices/subscriptionCategorySlice"
import { isAuth } from "./isAuth"
import { setAuthUser, signOutSuccess } from "store/slices/authSlice"
import { refreshAccessToken, isRefreshingToken } from "refreshToken"

const useSubscriptionCategories = () => {
  const [Categories, setCategories] = useState(null)
  const [Categorie, setCategorie] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const base_url = process.env.REACT_APP_API_URL;

  const url_getAll = `${base_url}/subscriptionCategories`;
  const url_add = `${base_url}/subscriptionCategories`;
  const url_update = `${base_url}/subscriptionCategories`;

  const dispatch = useDispatch()
  const tokenFromState = useSelector((state) => state.auth.token)

  if (localStorage.getItem(AUTH_TOKEN) != tokenFromState) {
    localStorage.removeItem(AUTH_TOKEN)
    dispatch(signOutSuccess())
    dispatch(setAuthUser({}))
  }

  // Fonction générique pour les appels API avec gestion du token
  const authFetch = async (url, options = {}, navigate) => {
    let token = tokenFromState || localStorage.getItem(AUTH_TOKEN)

    // Si pas de token et pas de refresh en cours, on tente un refresh
    if (!token && !isRefreshingToken()) {
      token = await refreshAccessToken()
    }

    if (!token) {
      throw new Error("Authentication required")
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })

    // Si token expiré, on tente un refresh
    if (response.status === 401) {
      token = await refreshAccessToken()
      if (!token) {
        localStorage.removeItem(AUTH_TOKEN)
        dispatch(signOutSuccess())
        dispatch(setAuthUser({}))
        if (navigate) navigate("/login")
        throw new Error("Session expired")
      }

      // On réessaye avec le nouveau token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
    }

    return response
  }

  // Gestion centralisée des erreurs API
  const handleApiError = (errorData) => {
    if (!isAuth(errorData)) {
      localStorage.removeItem(AUTH_TOKEN)
      dispatch(signOutSuccess())
      dispatch(setAuthUser({}))
    }

    if (errorData?.statusCode === 500) {
      setError("Erreur interne du serveur")
      return
    }

    if (Array.isArray(errorData?.data?.message)) {
      setError(errorData.data.message[0])
      return
    }

    if (errorData?.data?.message) {
      setError(errorData.data.message)
      return
    }

    if (errorData?.message) {
      setError(errorData.message)
      return
    }

    setError("Erreur inconnue")
  }

  const addCategory = async (data, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(
        url_add,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
        navigate,
      )

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setCategorie(responseData)
      await getAllCategories(navigate)
    } catch (err) {
      console.error("Failed to add category", err)
      setError(err.message || "Failed to add category")
    } finally {
      setIsLoading(false)
    }
  }

  const getAllCategories = async (navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(url_getAll, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseText = await response.text()
      const data = JSON.parse(responseText || "{}")

      if (Array.isArray(data.data)) {
        setCategories(data.data)
        dispatch(setCategoriesState(data.data))
      } else {
        console.error("❌ Les catégories ne sont pas un tableau :", data)
        setError("Les données des catégories sont invalides.")
      }
    } catch (err) {
      console.error("❌ Erreur lors de la récupération des catégories :", err)
      setError(err.message || "Failed to get categories")
    } finally {
      setIsLoading(false)
    }
  }

  const deleteCategory = async (id, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_add}/${id}`, { method: "DELETE" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      setCategorie([])
      await getAllCategories(navigate)
    } catch (err) {
      console.error("Failed to delete category", err)
      setError(err.message || "Failed to delete category")
    } finally {
      setIsLoading(false)
    }
  }

  const updateCategorie = async (data, id, navigate) => {
    setIsLoading(true)
    setError(null)

    const payload = {
      ...data,
      ageSectionId: Number(data.ageSectionId),
    }

    try {
      const response = await authFetch(
        `${url_update}/${id}`,
        {
          method: "PUT",
          body: JSON.stringify(payload),
        },
        navigate,
      )

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const responseData = await response.json()
      setCategorie(responseData)
      await getAllCategories(navigate)
    } catch (err) {
      console.error("Failed to update category", err)
      setError(err.message || "Failed to update category")
    } finally {
      setIsLoading(false)
    }
  }

  const getCategorieById = async (id, navigate) => {
    if (!id) return null

    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAll}/${id}`, { method: "GET" }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return null
      }

      const responseData = await response.json()
      const categoryData = responseData.data || responseData
      setCategorie(categoryData)
      return categoryData
    } catch (err) {
      console.error("Failed to get category", err)
      setError(err.message || "Failed to get category")
      return null
    } finally {
      setIsLoading(false)
    }
  }

  return {
    getCategorieById,
    deleteCategory,
    updateCategorie,
    addCategory,
    Categorie,
    Categories,
    getAllCategories,
    setCategories,
    setCategorie,
    isLoading,
    error,
    setError,
  }
}

export default useSubscriptionCategories